import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { Category } from '../../types'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { sortCategoriesByIndex } from '../menuSortOrder'

export const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__'
export const UNCATEGORIZED_NAME = 'Uncategorized'

const uncategorizedCategory: Category = {
  id: UNCATEGORIZED_CATEGORY_ID,
  name: UNCATEGORIZED_NAME,
  sortIndex: 0,
}

const categoriesCacheByPlace: Record<string, Category[]> = {}

function categoriesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'categories')
}

/** Firestore reserves doc IDs starting with __; uncategorized stored as 'uncategorized'. */
export function categoryFirestoreDocId(categoryId: string): string {
  return categoryId === UNCATEGORIZED_CATEGORY_ID ? 'uncategorized' : categoryId
}

function categoryDoc(placeId: string, categoryId: string) {
  return doc(
    getFirestoreInstance(),
    'places',
    placeId,
    'categories',
    categoryFirestoreDocId(categoryId),
  )
}

function docToCategory(d: QueryDocumentSnapshot): Category {
  const data = d.data()
  const id =
    d.id === 'uncategorized' ? UNCATEGORIZED_CATEGORY_ID : d.id
  const sortIndex =
    typeof data.sortIndex === 'number' && Number.isFinite(data.sortIndex)
      ? data.sortIndex
      : undefined
  return {
    id,
    name: (data.name as string) ?? '',
    sortIndex,
  }
}

export async function primeCategoriesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  const snap = await getDocs(categoriesRef(placeId))
  let list = snap.docs.map((d) => docToCategory(d))
  const hasUncategorized = list.some((c) => c.id === UNCATEGORIZED_CATEGORY_ID)
  if (!hasUncategorized) {
    list = [uncategorizedCategory, ...list]
    await setDoc(
      categoryDoc(placeId, UNCATEGORIZED_CATEGORY_ID),
      {
        id: UNCATEGORIZED_CATEGORY_ID,
        name: UNCATEGORIZED_NAME,
        sortIndex: 0,
      },
    )
  }
  categoriesCacheByPlace[placeId] = sortCategoriesByIndex(list)
}

export function getCategories(): Category[] {
  const placeId = getCurrentPlaceId()
  return categoriesCacheByPlace[placeId] ?? []
}

export function saveCategories(categories: Category[]): void {
  const placeId = getCurrentPlaceId()
  const withIndex = categories.map((c, i) => ({ ...c, sortIndex: i }))
  categoriesCacheByPlace[placeId] = withIndex
  ;(async () => {
    const snap = await getDocs(categoriesRef(placeId))
    const logicalIds = new Set(withIndex.map((c) => c.id))
    for (const d of snap.docs) {
      const logicalId =
        d.id === 'uncategorized' ? UNCATEGORIZED_CATEGORY_ID : d.id
      if (!logicalIds.has(logicalId)) await deleteDoc(d.ref)
    }
    for (const c of withIndex) {
      await setDoc(categoryDoc(placeId, c.id), {
        id: c.id,
        name: c.name,
        sortIndex: c.sortIndex ?? 0,
      })
    }
  })().catch((e) => console.error('Firestore saveCategories failed', e))
}

function generateCategoryId(): string {
  return `C-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addCategory(category: Omit<Category, 'id'>): Category {
  const placeId = getCurrentPlaceId()
  const id = generateCategoryId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  const maxIdx = cache.reduce((m, c) => Math.max(m, c.sortIndex ?? 0), -1)
  const full: Category = {
    ...category,
    id,
    sortIndex: maxIdx + 1,
  }
  categoriesCacheByPlace[placeId] = [...cache, full]
  setDoc(categoryDoc(placeId, id), {
    id: full.id,
    name: full.name,
    sortIndex: full.sortIndex ?? 0,
  }).catch((e) => console.error('Firestore addCategory failed', e))
  return full
}

export function updateCategory(id: string, patch: Partial<Category>): Category[] {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  categoriesCacheByPlace[placeId] = cache.map((c) =>
    c.id === id ? { ...c, ...patch } : c,
  )
  const found = categoriesCacheByPlace[placeId].find((c) => c.id === id)
  if (found) {
    setDoc(categoryDoc(placeId, id), {
      id: found.id,
      name: found.name,
      sortIndex: found.sortIndex ?? 0,
    }).catch((e) => console.error('Firestore updateCategory failed', e))
  }
  return categoriesCacheByPlace[placeId]
}

export function deleteCategory(id: string): Category[] {
  if (id === UNCATEGORIZED_CATEGORY_ID) return getCategories()
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  categoriesCacheByPlace[placeId] = cache.filter((c) => c.id !== id)
  deleteDoc(categoryDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteCategory failed', e),
  )
  return categoriesCacheByPlace[placeId]
}

export function reorderCategories(orderedIds: string[]): void {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  const byId = new Map(cache.map((c) => [c.id, c]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is Category => c != null)
  categoriesCacheByPlace[placeId] = newOrder
  saveCategories(newOrder)
}

export function isCategoriesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in categoriesCacheByPlace
}

/** Clear in-memory cache so the next user does not see previous account/place data. */
export function clearCategoriesCache(): void {
  Object.keys(categoriesCacheByPlace).forEach((k) => delete categoriesCacheByPlace[k])
}
